import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { useListInventoryVendors } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";

// Phase 2.1 polish E — vendor selector for the Stock-In Receipts form.
// Mirrors the ItemCombobox pattern: searchable popover that displays vendor
// names instead of forcing the operator to remember vendor IDs.

interface VendorComboboxProps {
  value: number | null;
  onChange: (id: number | null) => void;
  className?: string;
}

export function VendorCombobox({ value, onChange, className }: VendorComboboxProps) {
  const [open, setOpen] = useState(false);
  const { data: vendors = [] } = useListInventoryVendors();

  // 2026-05-20: show all vendors except blacklisted ones. Previously only "active"/"onboarding"
  // were selectable, which hid every "prospect" vendor — operators couldn't pick a prospect
  // vendor for Stock-In even though most vendors start as prospects.
  // (Vendor statuses: active | prospect | onboarding | on_hold | blacklisted.)
  const activeVendors = vendors.filter((v) => v.status !== "blacklisted");
  const selected = activeVendors.find((v) => v.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", className)}
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              <span className="truncate">{selected.vendorName}</span>
              {selected.vendorType ? (
                <span className="text-xs text-muted-foreground">· {selected.vendorType.replace(/_/g, " ")}</span>
              ) : null}
            </span>
          ) : (
            <span className="text-muted-foreground">Pick a vendor…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search vendor by name…" aria-label="Search vendors" />
          <CommandList>
            <CommandEmpty>No vendor matches.</CommandEmpty>
            <CommandGroup>
              {value !== null ? (
                <CommandItem value="__clear__" onSelect={() => { onChange(null); setOpen(false); }} className="text-muted-foreground italic">
                  Clear selection
                </CommandItem>
              ) : null}
              {activeVendors.map((v) => (
                <CommandItem
                  key={v.id}
                  value={v.vendorName}
                  onSelect={() => { onChange(v.id); setOpen(false); }}
                  className="py-2"
                >
                  <div className="flex w-full flex-col gap-0.5">
                    <span className="text-sm font-medium">{v.vendorName}</span>
                    {v.vendorType ? (
                      <span className="text-xs text-muted-foreground">{v.vendorType.replace(/_/g, " ")}</span>
                    ) : null}
                  </div>
                  {value === v.id ? <Check className="ml-2 h-4 w-4" /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
